import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { MenubarModule } from 'primeng/menubar';
import { DrawerModule } from 'primeng/drawer';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthService } from './core/auth/auth.service';
import { OauthCallbackComponent } from './core/oauth-callback/oauth-callback.component';
import { HeaderComponent } from './core/template/header/header.component';
import { LandingPageComponent } from './core/permissions/landing-page.component';
import { DatePipe } from '@angular/common';
import { SelectButtonModule } from 'primeng/selectbutton';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ButtonModule } from 'primeng/button';
import { LoaderComponent } from './core/loader/loader.component';
import { LoaderService } from './core/loader/loader.service';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { LoaderInterceptorService } from './core/loader/loader-interceptor';
import { DatePickerModule } from 'primeng/datepicker';
import { MessageModule } from 'primeng/message';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { DocumentsSearchComponent } from './modules/documents-search/documents-search.component';
import { TableModule } from 'primeng/table';
import {ProgressBarModule} from 'primeng/progressbar';
import { VehicleService } from './modules/services/vehicle.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SparePartsComponent } from './modules/spare-parts/spare-parts.component';
import { SparePartsService } from './modules/services/spare-parts.service';
import { SecurityGuard } from './core/permissions/security.guard.service';
import { UnauthorizeComponent } from './modules/unauthorize-page/unauthorize-page.component';
import { RetailReportsErrorHandler } from './core/permissions/retailReportsErrorHandler';
import { DealerStockListPageComponent } from './modules/dealer-stock/dealer-stock-list-page.component';
import { InvoiceService } from './modules/services/invoice-service';
import { VehicleListResolve } from './modules/resolves/vehicle-list.resolve';

// MSAL Imports
import { 
  MsalModule, 
  MsalService, 
  MsalGuard, 
  MsalInterceptor, 
  MsalBroadcastService, 
  MsalRedirectComponent,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration
} from '@azure/msal-angular';
import { 
  IPublicClientApplication, 
  PublicClientApplication, 
  InteractionType, 
  BrowserCacheLocation,
  LogLevel
} from '@azure/msal-browser';
import { environment } from '../environments/environment';

/**
 * MSAL Instance Factory
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: environment.msalConfig.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          console.log(message);
        },
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

/**
 * MSAL Guard Configuration Factory
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.myApiConfig.scopes],
    },
    loginFailedRoute: environment.msalConfig.loginFailedUri,
  };
}

/**
 * MSAL Interceptor Configuration Factory
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  
  // Protected resources - API ARFD
  protectedResourceMap.set(environment.myApiConfig.uri, environment.myApiConfig.scopes);
  
  // Microsoft Graph API (optional)
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

declare global{
  interface Navigator{
     msSaveBlob:(blob: Blob,fileName:string) => boolean
     }
  }
@NgModule({
  declarations: [
    AppComponent,
    OauthCallbackComponent,
    LoaderComponent,
    HeaderComponent,
    LandingPageComponent,
    DocumentsSearchComponent,
    SparePartsComponent,
    DealerStockListPageComponent,
    UnauthorizeComponent,
  ],
  imports: [
    MsalModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    MenubarModule,
    DrawerModule,
    DialogModule,
    ListboxModule,
    SelectButtonModule,
    BrowserAnimationsModule,
    ButtonModule,
    DatePickerModule,
    MessageModule,
    ToastModule,
    TooltipModule,
    AutoCompleteModule,
    SelectModule,
    CheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    TableModule,
    ProgressBarModule,
  ],
  providers: [
    SecurityGuard,
    AuthService,
    LoaderService,
    HttpClientModule,
    DatePipe,
    VehicleService,
    VehicleListResolve,
    MessageService,
    SparePartsService,
    RetailReportsErrorHandler,
    InvoiceService,
    ConfirmationService,
    // MSAL Providers
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptorService,
      multi: true,
    },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false
        }
      }
    }),
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  
})

export class AppModule {}
